/**
 * Persian-aware text folding for local option search (`docs/select-inputs.md` §2).
 *
 * The product is typed in Persian on Persian and Arabic keyboards, and the two layouts disagree on four things that a
 * naive substring match treats as different characters:
 *
 * - **the letter yeh**: Persian `ی` (U+06CC) and Arabic `ي` (U+064A) are both typed and both stored;
 * - **the letter kaf**: Persian `ک` (U+06A9) and Arabic `ك` (U+0643);
 * - **the digits**: Persian `۰-۹` (U+06F0), Arabic-Indic `٠-٩` (U+0660) and Latin `0-9`;
 * - **the marks**: the tatweel and the harakat that a stored title may carry and the user does not type.
 *
 * No normalizer existed in the SPA (the API normalizes what it stores and searches), so this is the one the shared select
 * owns: it is applied to **both** sides of a comparison — the term and the label — and never to a value, an identifier or
 * the text that is submitted, because folding is a *matching* rule, not a transformation of the user's input.
 */

/** The Arabic forms of the yeh and the kaf, folded onto the Persian letters. */
const YEH_VARIANTS = /[\u064A\u0649]/gu;
const KAF_VARIANT = /\u0643/gu;

/** Arabic-Indic digits, folded onto Latin digits. */
const ARABIC_INDIC_DIGITS = /[\u0660-\u0669]/gu;

/** Persian digits, folded onto Latin digits. */
const PERSIAN_DIGITS = /[\u06F0-\u06F9]/gu;

/** Marks that carry no searchable meaning: the combining harakat and the quranic marks above them. */
const COMBINING_MARKS = /\p{M}+/gu;

/** The tatweel, a letter-shaped elongation that neither keyboard expects in a search. */
const TATWEEL = /\u0640/gu;

/**
 * Folds a string onto the form both keyboards agree on.
 *
 * The result is used only for comparisons: `foldForSearch('كيف')` equals `foldForSearch('کیف')`, while the value that
 * travels to the API stays exactly what the user typed.
 *
 * @param text Text to fold — a search term or an option's label.
 * @returns The folded text, lower-cased as well so a Latin term matches regardless of case.
 */
export function foldForSearch(text: string): string {
  return text
    .replace(YEH_VARIANTS, '\u06CC')
    .replace(KAF_VARIANT, '\u06A9')
    .replace(ARABIC_INDIC_DIGITS, (digit) =>
      String.fromCharCode(digit.charCodeAt(0) - 0x0660 + 0x30),
    )
    .replace(PERSIAN_DIGITS, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0x06f0 + 0x30))
    .replace(TATWEEL, '')
    .replace(COMBINING_MARKS, '')
    .toLowerCase();
}

/**
 * Reports whether every word of a term appears in one of the labels, in any order.
 *
 * Word-wise rather than as one substring because a label often joins several facts
 * (`اتصال سازمانی · OpenAI · gpt-4o-mini`): a user searching `OpenAI gpt` finds it although the words are not adjacent.
 * **Every** word must match, so a term naming something the label does not carry finds nothing rather than everything.
 *
 * @param term Term the user typed.
 * @param labels Labels the option may be found by, the active locale's first.
 * @returns `true` when the option matches the term, and for an empty term.
 */
export function matchesSearchTerm(term: string, ...labels: (string | null | undefined)[]): boolean {
  const words = foldForSearch(term)
    .split(/\s+/u)
    .filter((word) => word.length > 0);

  if (words.length === 0) {
    return true;
  }

  const haystack = labels
    .filter((candidate): candidate is string => typeof candidate === 'string')
    .map(foldForSearch)
    .join(' ');

  return words.every((word) => haystack.includes(word));
}
